/**
 * Operator message waiting toasts + one device notification per newly received message.
 * In-app toast when the tab is visible; OS notification when the tab is in the background
 * (same stack as stock / Njord — Admin or Production, opt-in + permission + hours).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { useOverlayManager, OVERLAY_IDS } from '../../context/OverlayManagerContext';
import { db } from '../../services/firebase';
import {
    showAdminDesktopNotificationIfAllowed,
    userHasAdminOrProductionRole,
} from '../../services/adminDesktopNotificationService';
import {
    setPendingOperatorChatFocus,
    subscribeUnreadInbox,
} from '../../services/operatorChatService';
import ToastNotification from '../ToastNotification';
import '../ToastNotification.css';

const MAX_TOASTS = 3;
const SEEN_STORAGE_KEY = 'lsc_operator_msg_notified_ids';

function loadSeenIds() {
    try {
        const raw = sessionStorage.getItem(SEEN_STORAGE_KEY);
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        return new Set(Array.isArray(arr) ? arr.map(String) : []);
    } catch {
        return new Set();
    }
}

function persistSeenIds(set) {
    try {
        const arr = Array.from(set);
        const trimmed = arr.length > 400 ? arr.slice(-400) : arr;
        sessionStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(trimmed));
    } catch {
        /* ignore */
    }
}

function previewBody(item) {
    const text = String(item?.bodyText || '').trim();
    if (text) return text.slice(0, 140);
    const files = Array.isArray(item?.attachments) ? item.attachments : [];
    if (files.length === 1) return `File: ${files[0].fileName || 'attachment'}`;
    if (files.length > 1) return `${files.length} files`;
    return 'New operator message';
}

async function notifyDeviceWithFreshRoleCheck(userData, title, body, options) {
    const uid = userData?.uid;
    if (!uid) return false;
    try {
        const snap = await getDoc(doc(db, 'users', uid));
        const fresh = snap.exists() ? { uid, ...snap.data() } : { uid, role: 'Customer' };
        if (!userHasAdminOrProductionRole(fresh)) return false;
        return showAdminDesktopNotificationIfAllowed(fresh, title, body, options || {});
    } catch (e) {
        console.warn('[OperatorMessageNotifier] role re-check', e);
        return false;
    }
}

export default function OperatorMessageNotifier() {
    const { user } = useAuth();
    const { openOverlay } = useOverlayManager();
    const [userData, setUserData] = useState(null);
    const [toasts, setToasts] = useState([]);
    const seededRef = useRef(false);
    const seenIdsRef = useRef(loadSeenIds());
    const waitingHintShownRef = useRef(false);

    useEffect(() => {
        if (!user?.uid) {
            setUserData(null);
            seededRef.current = false;
            waitingHintShownRef.current = false;
            return undefined;
        }
        let cancelled = false;
        getDoc(doc(db, 'users', user.uid)).then((snap) => {
            if (cancelled) return;
            const data = snap.exists()
                ? { uid: user.uid, ...snap.data() }
                : { uid: user.uid, role: 'Customer' };
            setUserData(data);
        });
        return () => {
            cancelled = true;
        };
    }, [user?.uid]);

    const openMessages = useCallback(
        (chatId) => {
            if (chatId) setPendingOperatorChatFocus(chatId);
            openOverlay(OVERLAY_IDS.OPERATOR_MESSAGES);
        },
        [openOverlay]
    );

    useEffect(() => {
        if (!user?.uid || !userData || !userHasAdminOrProductionRole(userData)) {
            return undefined;
        }

        return subscribeUnreadInbox(user.uid, (items) => {
            const list = Array.isArray(items) ? items : [];
            const currentIds = new Set(list.map((item) => String(item.id)));

            if (!seededRef.current) {
                seededRef.current = true;
                currentIds.forEach((id) => seenIdsRef.current.add(id));
                persistSeenIds(seenIdsRef.current);

                // Soft in-app hint if messages are already waiting (no device spam on load).
                if (list.length > 0 && !waitingHintShownRef.current) {
                    waitingHintShownRef.current = true;
                    const newest = list[list.length - 1] || list[0];
                    setToasts((prev) => {
                        const toast = {
                            id: `op-msg-waiting-${Date.now()}`,
                            title:
                                list.length === 1
                                    ? 'Message waiting'
                                    : `${list.length} messages waiting`,
                            message:
                                list.length === 1
                                    ? previewBody(newest)
                                    : 'Open Messages from the Quick menu to catch up.',
                            type: 'info',
                            duration: 7000,
                            position: 'top-right',
                            ctaText: 'Open Messages',
                            ctaLink: '/messages',
                            skipNavigate: true,
                            chatId: newest?.chatId || null,
                        };
                        const updated = [...prev, toast];
                        return updated.length > MAX_TOASTS ? updated.slice(-MAX_TOASTS) : updated;
                    });
                }
                return;
            }

            const fresh = list.filter((item) => !seenIdsRef.current.has(String(item.id)));
            if (!fresh.length) return;

            fresh.forEach((item) => {
                const id = String(item.id);
                seenIdsRef.current.add(id);
                const title = item.isSelfNote
                    ? 'Reminder for you'
                    : `Message from ${item.fromName || 'Operator'}`;
                const body = previewBody(item);
                const tag = `lsc-operator-msg-${id}`;
                const chatId = item.chatId || null;

                // Device notification only for newly received messages, and only when the tab
                // is in the background (same pattern as stock / Njord). Visible tab → little toast.
                if (typeof document !== 'undefined' && document.hidden) {
                    void notifyDeviceWithFreshRoleCheck(userData, title, body, {
                        tag,
                        onClick: () => openMessages(chatId),
                    }).then((shown) => {
                        if (!shown) {
                            setToasts((prev) => {
                                const toast = {
                                    id: tag,
                                    title,
                                    message: body,
                                    type: 'info',
                                    duration: 8000,
                                    position: 'top-right',
                                    ctaText: 'Open Messages',
                                    ctaLink: '/messages',
                                    skipNavigate: true,
                                    chatId,
                                };
                                const updated = [...prev, toast];
                                return updated.length > MAX_TOASTS
                                    ? updated.slice(-MAX_TOASTS)
                                    : updated;
                            });
                        }
                    });
                } else {
                    setToasts((prev) => {
                        const toast = {
                            id: tag,
                            title,
                            message: body,
                            type: 'info',
                            duration: 8000,
                            position: 'top-right',
                            ctaText: 'Open Messages',
                            ctaLink: '/messages',
                            skipNavigate: true,
                            chatId,
                        };
                        const updated = [...prev, toast];
                        return updated.length > MAX_TOASTS ? updated.slice(-MAX_TOASTS) : updated;
                    });
                }
            });

            persistSeenIds(seenIdsRef.current);
        });
    }, [user?.uid, userData, openMessages]);

    const handleDismiss = useCallback((toastId) => {
        setToasts((prev) => prev.filter((t) => t.id !== toastId));
    }, []);

    const handleCTAClick = useCallback(
        (_id, _link, toast) => {
            openMessages(toast?.chatId || null);
        },
        [openMessages]
    );

    if (!userData || !userHasAdminOrProductionRole(userData)) {
        return null;
    }

    return (
        <div className="toast-container toast-top-right op-msg-toast-layer">
            {toasts.map((toast) => (
                <ToastNotification
                    key={toast.id}
                    notification={toast}
                    onDismiss={handleDismiss}
                    onCTAClick={(id, link) => handleCTAClick(id, link, toast)}
                    position="top-right"
                />
            ))}
        </div>
    );
}
